/**
 * Public Support Portal — for SaarOne customers / prospects.
 *
 * Path:
 *   /support               → new ticket form
 *   /support/ticket/:token → view + reply thread
 *
 * No authentication required. Token-based access (magic link).
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, Envelope, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/support/public`;

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-zinc-900">SaarOne</Link>
          <span className="text-xs text-zinc-500 inline-flex items-center gap-1">
            <ShieldCheck size={14} /> Customer support
          </span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}

export default function SupportPortal() {
  const [form, setForm] = useState({ email: "", name: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/tickets`, form);
      setCreated(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't submit. Try again in a minute.");
    } finally { setBusy(false); }
  };

  if (created) return (
    <Shell>
      <Card className="p-8" data-testid="support-success">
        <CheckCircle size={36} weight="duotone" className="mx-auto text-emerald-600 mb-3" />
        <div className="text-center font-semibold text-lg text-zinc-900">Ticket received</div>
        <div className="text-sm text-zinc-600 mt-2 text-center">
          We&apos;ve emailed you a confirmation. Your ticket number is{" "}
          <span className="font-mono font-semibold">{created.number}</span>.
        </div>
        <div className="mt-6 flex justify-center">
          <Link to={`/support/ticket/${created.token}`}>
            <Button data-testid="view-ticket-btn">View your ticket →</Button>
          </Link>
        </div>
      </Card>
    </Shell>
  );

  return (
    <Shell>
      <h1 className="text-2xl font-semibold text-zinc-900">Contact support</h1>
      <p className="text-sm text-zinc-600 mt-1">Tell us what&apos;s going on. We typically reply within one business day.</p>

      <Card className="p-6 mt-6" data-testid="support-form-card">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Your name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="name-input" />
            </div>
            <div>
              <Label>Work email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="email-input" />
            </div>
          </div>
          <div>
            <Label>Subject</Label>
            <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Cannot generate payslips for May" data-testid="subject-input" />
          </div>
          <div>
            <Label>Describe the issue</Label>
            <Textarea required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Include steps to reproduce, error messages, screenshots if any." data-testid="body-input" />
          </div>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <div className="flex justify-end">
            <Button type="submit" disabled={busy} data-testid="submit-ticket-btn">{busy ? "Submitting…" : "Submit ticket"}</Button>
          </div>
        </form>
      </Card>
      <p className="text-xs text-zinc-500 mt-4">
        You can also reply directly to our emails — replies will land on the same ticket.
      </p>
    </Shell>
  );
}

export function SupportTicketView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/tickets/${token}`);
      setState({ loading: false, ...r.data });
    } catch (e) {
      setState({ loading: false, error: e?.response?.status === 404 ? "Ticket not found" : "Couldn't load ticket" });
    }
  };
  useEffect(() => { load(); }, [token]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/tickets/${token}/reply`, { body: reply });
      setReply("");
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't send reply");
    } finally { setSending(false); }
  };

  if (state.loading) return <Shell><Card className="p-6 text-sm text-zinc-500">Loading…</Card></Shell>;
  if (state.error) return <Shell><Card className="p-6 text-sm text-red-600">{state.error}</Card></Shell>;
  const { ticket, messages } = state;
  const isClosed = ticket.status === "closed";
  return (
    <Shell>
      <button onClick={() => navigate("/support")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-3"><ArrowLeft size={14} /> Open another ticket</button>
      <Card className="p-6" data-testid="ticket-view">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs text-zinc-500 font-mono">{ticket.number}</div>
            <h1 className="text-xl font-semibold text-zinc-900 mt-1">{ticket.subject}</h1>
            <div className="text-xs text-zinc-500 mt-1">
              {ticket.customer_name} &middot; opened {(ticket.created_at || "").slice(0, 16).replace("T", " ")}
            </div>
          </div>
          <Badge variant={ticket.status === "open" ? "default" : ticket.status === "resolved" ? "secondary" : "outline"} data-testid="ticket-status">{ticket.status}</Badge>
        </div>

        <div className="mt-6 space-y-4">
          {messages.map((m) => (
            <div key={m.id} className={`p-3 rounded-lg ${m.author_type === "customer" ? "bg-zinc-100" : m.author_type === "staff" ? "bg-indigo-50" : "bg-amber-50 italic text-xs"}`} data-testid={`msg-${m.id}`}>
              <div className="text-xs text-zinc-500 mb-1">
                <span className="font-semibold text-zinc-700">{m.author_name}</span>
                {" · "}
                {(m.created_at || "").slice(0, 16).replace("T", " ")}
                {m.channel === "email" && <span className="ml-1 text-zinc-400">(via email)</span>}
              </div>
              <div className="whitespace-pre-wrap text-sm text-zinc-800">{m.body}</div>
            </div>
          ))}
        </div>

        {isClosed ? (
          <div className="mt-6 p-3 rounded bg-zinc-100 text-sm text-zinc-600">
            This ticket is closed. <Link to="/support" className="underline">Open a new one</Link> if you need more help.
          </div>
        ) : (
          <form onSubmit={send} className="mt-6">
            <Label>Add a reply</Label>
            <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} className="mt-1" data-testid="reply-input" />
            <div className="flex justify-end mt-2">
              <Button type="submit" disabled={sending || !reply.trim()} data-testid="send-reply-btn">{sending ? "Sending…" : "Send reply"}</Button>
            </div>
          </form>
        )}
      </Card>
    </Shell>
  );
}
